// src/pages/remessa/UpsWizard.tsx
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
    verCaixas,
    verClienteAtual,
    criarRemessa,
    atualizarTrackRemessa,
    criarCotacao,
    montarPayloadCotacaoReal,
    anexarDocsCotacao,
    verCotacaoDetalhes,
    compararFretesShipment,
    confirmarFreteShipment,
} from "services/api";

import { cotarUPS, emitirUPS, rastrearUPS } from "services/ups";
import type { UpsRateRequest, UpsShipRequest } from "types/upsTypes";

import { cotarFEDEX, emitirFEDEX, rastrearFEDEX } from "services/fedex";
import type { FedexRequest, FedexShipResponseRaw } from "types/fedexTypes";

import RateStep, { CaixaCotacao, CaixaSnapshot } from "./steps/RateStep";
import ShipStep from "./steps/ShipStep";
import TrackStep from "./steps/TrackStep";

import { onlyDigits, toNum, uid } from "./utils";

type Endereco = {
    nome: string;
    telefone: string;
    rua: string;
    numero: string;
    complemento?: string;
    cidade: string;
    estado: string;
    cep: string;
    pais: string;
    cnpjOuTaxId?: string;
    email: string;
};

const SHIPPER_NUMBER = process.env.REACT_APP_UPS_ACCOUNT_NUMBER || undefined;

// -------------------- helpers de log --------------------
function safeJsonParse(s?: any) {
    try {
        return typeof s === "string" ? JSON.parse(s) : s;
    } catch {
        return s;
    }
}
function logAxiosError(scope: string, err: any) {
    const isAxios = !!err && (err.isAxiosError || err?.response || err?.config);
    if (!isAxios) {
        console.error(`[${scope}]`, err);
        return;
    }
    const status = err?.response?.status;
    const url = err?.config?.url;
    const method = err?.config?.method;
    const reqData = safeJsonParse(err?.config?.data);
    const resData = err?.response?.data;
    const headers = err?.response?.headers;

    console.groupCollapsed(
        `[${scope}] AxiosError ${status ?? ""} ${method?.toUpperCase?.() || ""} ${url || ""}`
    );
    console.log("message:", err?.message);
    console.log("request:", { method, url, data: reqData });
    console.log("response:", {
        status,
        headers,
        data: typeof resData === "string" ? resData.slice(0, 2000) : resData,
    });
    if (typeof err?.toJSON === "function") {
        try {
            console.log("toJSON:", err.toJSON());
        } catch { }
    }
    console.groupEnd();
}

function onlyDigitsStr(s: any) {
    return String(s ?? "").replace(/\D/g, "");
}
function cleanZip(v?: any) {
    return String(v ?? "").replace(/\D/g, "");
}

// ==== HELPERS GERAIS ====
function num(x: any) {
    if (x == null) return 0;
    const s = String(x).trim().replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
}
function toYMD(d?: string | Date | null): string | null {
    if (!d) return null;
    if (d instanceof Date) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}${m}${day}`;
    }
    const s = String(d).trim();
    const m = s.match(/^(\d{4})[-]?(\d{2})[-]?(\d{2})$/);
    if (m) return `${m[1]}${m[2]}${m[3]}`;
    const dd = new Date(s);
    return isNaN(dd.getTime()) ? null : toYMD(dd);
}
function moneyStr(v: any): string {
    const n = Number(v);
    return isNaN(n) ? "0.00" : n.toFixed(2);
}
function qtyStr(n: any): string {
    const x = parseInt(String(n ?? "").replace(/\D+/g, ""), 10);
    if (!x || x < 1) return "1";
    return String(Math.min(x, 9_999_999));
}
function addrArr(rua?: string, numero?: string): string[] {
    const l1 = [String(rua || "").trim(), String(numero || "").trim()].filter(Boolean).join(", ");
    return [l1.slice(0, 35)];
}
function firstStr(...vals: any[]) {
    for (const v of vals) {
        if (v === 0) return "0";
        if (typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
}
function guessNumberFromStreet(street: string) {
    const m = (street || "").match(/\b(\d{1,6})\b/);
    return m ? m[1] : "";
}
export function iso2Country(v?: any) {
    const x = String(v ?? "").trim().toUpperCase();
    const map: Record<string, string> = {
        BR: "BR",
        BRA: "BR",
        BRASIL: "BR",
        BRAZIL: "BR",
        US: "US",
        USA: "US",
        "UNITED STATES": "US",
        UNITEDSTATES: "US",
        CA: "CA",
        CANADA: "CA",
        MX: "MX",
        MEXICO: "MX",
        AR: "AR",
        ARGENTINA: "AR",
        CL: "CL",
        CHILE: "CL",
    };
    return map[x] || (x.length === 2 ? x : "");
}

// ==== UPS helpers ====
function n2u<T>(v: T | null | undefined): T | undefined {
    return v == null ? undefined : v;
}
function itensArray(pedido: any): any[] {
    if (!pedido) return [];
    if (Array.isArray(pedido.itens)) return pedido.itens;
    if (Array.isArray(pedido.items)) return pedido.items;
    if (Array.isArray(pedido.line_items)) return pedido.line_items;
    return [];
}
function getItensTotalKg(pedido: any): number {
    const manual = num(pedido?.peso_total_kg);
    if (manual > 0) return manual;

    const itens = Array.isArray(pedido?.itens) ? pedido.itens : [];
    return itens.reduce((acc: number, it: any) => {
        const candidates: any[] = [
            it.peso_kg,
            it.weightKg,
            it.grams != null ? Number(it.grams) / 1000 : undefined,
            it.pesoUnit === "" ? undefined : it.pesoUnit,
            it.peso === "" ? undefined : it.peso,
            it.pesoBruto === "" ? undefined : it.pesoBruto,
        ];
        const unitKg = candidates.map(num).find((v) => v > 0) || 0;
        const qty = num(it.qty ?? it.quantidade ?? 1) || 1;
        return acc + qty * unitKg;
    }, 0);
}

function fromClienteToEndereco(c: any): Endereco {
    const paisISO = iso2Country(c?.enderecoPais || "BR") || "BR";
    const tax =
        c?.cnpj ??
        c?.tax_id ??
        c?.ein ??
        c?.vat ??
        c?.vat_number ??
        c?.ie ??
        c?.inscricao_estadual ??
        c?.eori ??
        "";
    return {
        nome: (c?.razaoSocial || c?.nomeFantasia || "Remetente").toString(),
        telefone: (c?.telefoneCelular || c?.telefone || "").toString(),
        rua: (c?.enderecoRua || "").toString(),
        numero: (c?.enderecoNumero || "").toString(),
        complemento: c?.enderecoComplemento || undefined,
        cidade: (c?.enderecoCidade || "").toString(),
        estado: (c?.enderecoEstado || "").toString(),
        cep: onlyDigitsStr(c?.enderecoCEP),
        pais: paisISO,
        cnpjOuTaxId: (tax || "").toString() || undefined,
        email: (c?.enderecoEmail || "").toString(),
    };
}
function fromPedidoToEndereco(pedido: any): Endereco {
    const e = pedido?.endereco;
    const is = e && typeof e === "object";
    const sa = pedido?.shipping_address || pedido?.shippingAddress || {};

    const rua = firstStr(
        is && (e.rua || e.endereco || e.address1),
        typeof e === "string" ? e : "",
        pedido?.enderecoRua,
        pedido?.ruaENum,
        pedido?.endereco,
        pedido?.address1,
        pedido?.rua_e_numero,
        pedido?.rua,
        sa?.address1,
        Array.isArray(sa?.addressLine) && sa.addressLine[0]
    );
    const numero =
        firstStr(is && (e.numero || e.num), pedido?.enderecoNumero) || guessNumberFromStreet(rua);
    const complemento =
        firstStr(
            is && (e.complemento || e.address2),
            pedido?.enderecoComplemento,
            sa?.address2,
            Array.isArray(sa?.addressLine) && sa.addressLine[1]
        ) || undefined;

    const cidade = firstStr(is && e.cidade, pedido?.cidade, pedido?.city, sa?.city);
    const estado = firstStr(
        is && (e.estado || e.provincia || e.province || e.state),
        pedido?.estado,
        pedido?.estado_provincia,
        sa?.province_code,
        sa?.province,
        sa?.state
    );
    const cep = cleanZip(firstStr(is && (e.cep || e.zip), pedido?.CEP, pedido?.cep, sa?.zip));

    const paisRaw =
        firstStr(
            typeof pedido?.endereco === "object" && (pedido.endereco.pais || pedido.endereco.country),
            pedido?.pais,
            pedido?.shipping_address?.country_code_v2,
            pedido?.shipping_address?.country_code,
            pedido?.shipping_address?.country
        ) || "US";
    const pais = iso2Country(paisRaw) || "US";

    const telefone = firstStr(pedido?.telefoneComprador, pedido?.telefone, sa?.phone);
    const nome = firstStr(pedido?.nomeComprador, pedido?.nome_completo, sa?.name, pedido?.name);
    const email = firstStr(pedido?.email, pedido?.emailRemetente, pedido?.email_remetente);

    return { nome, telefone, rua, numero, complemento, cidade, estado, cep, pais, email };
}

// ==== Compare types ====
type CompareRow = {
    id?: number | string;
    carrier: "UPS" | "FEDEX";
    serviceCode?: string;
    label: string;
    currency: string;
    total: number;
    raw?: any;
    base?: number;
    /** o que o cliente paga: (frete + taxas) com o markup do plano, vem do compare */
    total_cliente?: number;
    carrier_raw?: any; // importante: vai pro confirm
};

// RATE FEDEX (mínimo)
type FedexRateRequest = {
    pedidoId: string;
    packagesId: string;
    pesoTotalPedidoKg: number;
    serviceCode?: string;
    pickupDate?: string;
    moeda?: string;
};

// =============================================
// CompareStep
// =============================================
function CompareStep({
    rows,
    loading,
    erro,
    onBack,
    onSelectCarrier,
}: {
    rows: CompareRow[];
    loading: boolean;
    erro: string | null;
    onBack: () => void;
    onSelectCarrier: (row: CompareRow) => void;
}) {
    const [displayById, setDisplayById] = useState<
        Record<string | number, { total: number; currency: string }>
    >({});
    const fetchingRef = useRef<Record<string | number, boolean>>({});
    const abortRef = useRef<AbortController | null>(null);

    // useEffect(() => {
    //     if (!rows?.length) return;
    //     const ac = new AbortController();
    //     abortRef.current = ac;

    //     (async () => {
    //         const updates: Record<string | number, { total: number; currency: string }> = {};
    //         const tasks: Promise<void>[] = [];

    //         for (const r of rows) {
    //             const id = (r as any).id ?? (r as any).cotacao_id ?? (r as any).uuid;
    //             if (id == null) continue;

    //             if (displayById[id] || fetchingRef.current[id]) continue;
    //             fetchingRef.current[id] = true;

    //             tasks.push(
    //                 verCotacaoDetalhes(id)
    //                     .then((res: any) => {
    //                         const d = (res?.data?.data || {}) as {
    //                             total?: number;
    //                             currency?: string;
    //                             base?: number;
    //                             itemized?: Array<{ value?: number }>;
    //                             compare_total?: number;
    //                         };

    //                         let total: number | undefined = Number(d?.compare_total);
    //                         if (!Number.isFinite(total)) total = Number(d?.base) || 0;

    //                         if (Number.isFinite(total) && total > 0) {
    //                             updates[id] = { total, currency: d?.currency || "USD" };
    //                         }
    //                     })
    //                     .catch(() => { })
    //                     .finally(() => {
    //                         fetchingRef.current[id] = false;
    //                     })
    //             );
    //         }

    //         await Promise.all(tasks);
    //         if (Object.keys(updates).length) setDisplayById((prev) => ({ ...prev, ...updates }));
    //     })();

    //     return () => abortRef.current?.abort();
    // }, [rows, displayById]);

    const priceFor = (r: CompareRow) => {
        const id = (r as any).id ?? (r as any).cotacao_id ?? (r as any).uuid;
        const disp = id != null ? displayById[id] : null;
        if (disp && Number.isFinite(disp.total)) return { total: disp.total, currency: disp.currency || r.currency, base: disp.total, cliente: disp.total };
        // cliente = (frete + taxas) com o markup do plano, como vem do compare
        return { total: r.total, currency: r.currency, base: r.base, cliente: r.total_cliente ?? r.total ?? r.base ?? 0 };
    };

    return (
        <div id="page-ups">
            <div id="card-ups">
                <h2>3 - Comparar fretes</h2>

                {loading ? <p>Calculando cotações…</p> : null}
                {erro ? <div style={{ color: "crimson", marginBottom: 8 }}>{erro}</div> : null}

                {rows.length === 0 && !loading && !erro ? (
                    <p>Nenhuma cotação disponível.</p>
                ) : (
                    <div style={{ display: "grid", gap: 8 }}>
                        {rows.map((r, i) => {
                            const p = priceFor(r);
                            return (
                                <div
                                    key={i}
                                    style={{
                                        border: "1px solid #ddd",
                                        borderRadius: 6,
                                        padding: 10,
                                        display: "grid",
                                        gridTemplateColumns: "1fr auto",
                                        alignItems: "center",
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>
                                            {r.carrier} {r.serviceCode ? `(${r.serviceCode})` : ""}
                                        </div>
                                        <div style={{ opacity: 0.8 }}>{r.label}</div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                                            {Number(p.cliente).toFixed(2)} {p.currency} (frete + taxas)
                                        </div>
                                        <button
                                            style={{ marginTop: 6 }}
                                            className="botao botao--primario"
                                            onClick={() => onSelectCarrier(r)}
                                        >
                                            {loading ? "Carregando..." : "Escolher"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={{ marginTop: 16 }}>
                    <button className="botao botao--fantasma" onClick={onBack}>
                        Voltar
                    </button>
                </div>
            </div>
        </div>
    );
}

// ===== FEDEX helpers (Ship raw -> tracking + docs) =====
function fedexExtractTrackingNumbers(ship: FedexShipResponseRaw): string[] {
    const out = ship?.raw?.output;
    const tx = out?.transactionShipments?.[0];
    const master = tx?.masterTrackingNumber ? [String(tx.masterTrackingNumber)] : [];

    const pieces = Array.isArray(tx?.pieceResponses)
        ? tx!.pieceResponses!
            .map((p) => p?.trackingNumber)
            .filter(Boolean)
            .map(String)
        : [];

    const all = [...master, ...pieces];
    return Array.from(new Set(all));
}

function extractFedexArtifacts(ship: FedexShipResponseRaw): {
    trackingNumbers: string[];
    labelUrl?: string;
    invoiceUrl?: string;
    labelMime?: string;
    invoiceMime?: string;
} {
    const ts = ship?.raw?.output?.transactionShipments?.[0];
    if (!ts) return { trackingNumbers: [] };

    const trackingNumbers = [
        ts?.masterTrackingNumber,
        ...(Array.isArray(ts?.pieceResponses) ? ts.pieceResponses.map((p: any) => p?.trackingNumber) : []),
    ].filter(Boolean);

    const shipmentDocs = Array.isArray(ts?.shipmentDocuments) ? ts.shipmentDocuments : [];
    const invoiceDoc =
        shipmentDocs.find((d: any) => d?.contentType === "COMMERCIAL_INVOICE") || shipmentDocs[0];

    const piece = Array.isArray(ts?.pieceResponses) ? ts.pieceResponses[0] : null;
    const pkgDocs = piece && Array.isArray(piece.packageDocuments) ? piece.packageDocuments : [];
    const labelDoc = pkgDocs.find((d: any) => d?.contentType === "LABEL") || pkgDocs[0];

    return {
        trackingNumbers,
        labelUrl: labelDoc?.url,
        labelMime: labelDoc?.docType === "PDF" ? "application/pdf" : undefined,
        invoiceUrl: invoiceDoc?.url,
        invoiceMime: invoiceDoc?.docType === "PDF" ? "application/pdf" : undefined,
    };
}

export default function UpsWizard() {
    // 🔁 Etapas: 1=Rate, 2=Ship, 3=Compare, 4=Track
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

    // ---- Rating ----
    const [moeda, setMoeda] = useState("USD");
    const [serviceCode, setServiceCode] = useState("08");
    const [pickupDate, setPickupDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

    const [todasCaixas, setTodasCaixas] = useState<CaixaSnapshot[]>([]);
    const [codSelecionado, setCodSelecionado] = useState("");
    const [cotacaoCaixas, setCotacaoCaixas] = useState<CaixaCotacao[]>([]);
    const [pedidoRef, setPedidoRef] = useState("");
    const [pedidoObj, setPedidoObj] = useState<any | null>(null);

    // qual carrier foi emitido (pra tracking)
    const [selectedCarrier, setSelectedCarrier] = useState<"UPS" | "FEDEX">("UPS");

    const [ratingResp, setRatingResp] = useState<any | null>(null);
    const [cotacaoId, setCotacaoId] = useState<number | null>(null);

    // ---- Shipping ----
    const [remetente, setRemetente] = useState<Endereco>({
        nome: "",
        telefone: "",
        rua: "",
        numero: "",
        cidade: "",
        estado: "",
        cep: "",
        pais: "BR",
        cnpjOuTaxId: "",
        email: "",
    });
    const [destinatario, setDestinatario] = useState<Endereco>({
        nome: "",
        telefone: "",
        rua: "",
        numero: "",
        complemento: "",
        cidade: "",
        estado: "",
        cep: "",
        pais: "",
        email: "",
    });
    const [paymentBill, setPaymentBill] = useState<"Shipper" | "Receiver" | "ThirdParty">("Shipper");
    const [triangulacao, setTriangulacao] = useState<"DAP" | "DDP" | "DDU">("DAP");

    // ---- Emissão ----
    const [emitLoading, setEmitLoading] = useState(false);
    const [emitErro, setEmitErro] = useState<string | null>(null);

    type EmitResp = {
        carrier: "UPS" | "FEDEX";
        trackingNumbers: string[];

        // UPS (base64)
        labelB64?: string;
        labelType?: string;
        invoiceB64?: string;
        invoiceMime?: string;

        // FEDEX (url)
        labelUrl?: string;
        invoiceUrl?: string;
    };
    const [emitResp, setEmitResp] = useState<EmitResp | null>(null);
    const [shipmentId, setShipmentId] = useState<number | null>(null);

    // ---- Tracking ----
    const [tracking, setTracking] = useState("");
    const [trackLoading, setTrackLoading] = useState(false);
    const [trackErro, setTrackErro] = useState<string | null>(null);
    const [trackResp, setTrackResp] = useState<any | null>(null);

    // ---- Compare ----
    const [compareRows, setCompareRows] = useState<CompareRow[]>([]);
    const [compareLoading, setCompareLoading] = useState(false);
    const [compareErro, setCompareErro] = useState<string | null>(null);

    // carregar cadastro remetente
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await verClienteAtual();
                if (!alive) return;
                setRemetente(fromClienteToEndereco(data));
            } catch { }
        })();
        return () => {
            alive = false;
        };
    }, []);

    // carregar caixas
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await verCaixas();
                const arr = Array.isArray(res?.data) ? (res.data as any as CaixaSnapshot[]) : [];
                if (!alive) return;
                setTodasCaixas(arr);
            } catch { }
        })();
        return () => {
            alive = false;
        };
    }, []);

    // pedido selecionado → destinatário
    useEffect(() => {
        if (!pedidoObj) return;
        setDestinatario(fromPedidoToEndereco(pedidoObj));
    }, [pedidoObj]);

    // após emitir → tracking → Step 4
    useEffect(() => {
        const tn = emitResp?.trackingNumbers?.[0];
        if (tn) {
            setTracking(tn);
            setStep(4);
        }
    }, [emitResp]);

    const mapaPorCod = useMemo(() => {
        const m = new Map<string, CaixaSnapshot>();
        for (const c of todasCaixas) if (c.cod_identificacao) m.set(String(c.cod_identificacao), c);
        return m;
    }, [todasCaixas]);

    const addCaixa = () => {
        if (!codSelecionado) return;
        const found = mapaPorCod.get(codSelecionado);
        if (!found) return;
        setCotacaoCaixas((prev) => [{ entryId: `entry_${uid()}`, cod_identificacao: codSelecionado, data: found }, ...prev]);
    };
    const removeCaixa = (id: string) => setCotacaoCaixas((prev) => prev.filter((c) => c.entryId !== id));

    // ========= COMPARAÇÃO UPS + FEDEX =========
    async function handleCompare() {
        setCompareErro(null);
        setCompareRows([]);
        setCompareLoading(true);

        try {
            if (!cotacaoCaixas.length) throw new Error("Adicione ao menos uma caixa.");
            if (!pedidoRef) throw new Error("Selecione/informe um pedido.");

            const okPessoa = (p: any) => p.nome && p.rua && p.cidade && p.cep && p.pais;
            if (!okPessoa(remetente)) throw new Error("Cadastro do remetente incompleto.");
            if (!okPessoa(destinatario)) throw new Error("Pedido sem endereço completo.");

            const itensTotalKg = getItensTotalKg(pedidoObj);
            if (!itensTotalKg || itensTotalKg <= 0) {
                throw new Error("Informe o peso total dos itens (kg) no topo ou cadastre o peso dos produtos.");
            }

            const nPkgs = cotacaoCaixas.length || 1;
            const pesoPorCaixa = Math.max(itensTotalKg / nPkgs, 0.001);

            const packages = cotacaoCaixas.map((c) => ({
                weightKg: pesoPorCaixa,
                dimCm: {
                    height: toNum(c.data.altura) ?? 0,
                    width: toNum(c.data.largura) ?? 0,
                    length: toNum(c.data.comprimento) ?? 0,
                },
            }));

            // ===== UPS payload =====
            const upsPackages: UpsRateRequest["RateRequest"]["Shipment"]["Package"] = packages.map((p) => ({
                PackagingType: { Code: "02" },
                Dimensions: {
                    UnitOfMeasurement: { Code: "CM" },
                    Length: String(p.dimCm.length || 0),
                    Width: String(p.dimCm.width || 0),
                    Height: String(p.dimCm.height || 0),
                },
                PackageWeight: { UnitOfMeasurement: { Code: "KGS" }, Weight: String(Math.max(p.weightKg, 0.001)) },
            }));

            // total declarado (se não tiver itens, coloca 1.00)
            const invoiceItems = itensArray(pedidoObj);
            const declaredTotalNum =
                invoiceItems.reduce((acc: number, it: any) => {
                    const q = Number(it.qty || it.quantidade || 1) || 1;
                    const p = Number(it.preco || it.unit_price || it.price || 0) || 0;
                    const line = Number(it.valorTotalLinha || 0) || p * q;
                    return acc + (Number.isFinite(line) ? line : 0);
                }, 0) || 1;
            const declaredTotal = declaredTotalNum.toFixed(2);

            const pickupYMD = (pickupDate || "").replace(/-/g, "");
            const shipperAddressLine = [remetente?.rua, remetente?.numero].filter(Boolean).join(", ");
            const shipToAddressLine = [destinatario?.rua, destinatario?.numero].filter(Boolean).join(", ");
            console.log("ESTADO:", remetente.estado)

            const upsRatePayload: UpsRateRequest = {
                RateRequest: {
                    Request: {
                        RequestOption: "Rate",
                        TransactionReference: { CustomerContext: "compare", TransactionIdentifier: "String" },
                    },
                    PickupType: { Code: "01" },
                    CustomerClassification: { Code: "01" },
                    Shipment: {
                        Shipper: {
                            ShipperNumber: SHIPPER_NUMBER || "K9E789",
                            TaxIdentificationNumber: remetente?.cnpjOuTaxId ?? "",
                            Address: {
                                AddressLine: [shipperAddressLine || ""],
                                City: remetente?.cidade || "",
                                StateProvinceCode: (remetente?.estado || "").toUpperCase(),
                                PostalCode: onlyDigits(remetente?.cep) || "",
                                CountryCode: iso2Country(remetente?.pais) || "",
                            },
                        },
                        ShipFrom: {
                            Address: {
                                AddressLine: [shipperAddressLine || ""],
                                City: remetente?.cidade || "",
                                StateProvinceCode: (remetente?.estado || "").toUpperCase(),
                                PostalCode: onlyDigits(remetente?.cep) || "",
                                CountryCode: iso2Country(remetente?.pais) || "",
                            },
                        },
                        ShipTo: {
                            Address: {
                                AddressLine: [shipToAddressLine || ""],
                                City: destinatario?.cidade || "",
                                StateProvinceCode: (destinatario?.estado || "").toUpperCase(),
                                PostalCode: onlyDigits(destinatario?.cep) || "",
                                CountryCode: iso2Country(destinatario?.pais) || "",
                            },
                        },
                        Service: { Code: serviceCode || "" },
                        ShipmentTotalWeight: {
                            UnitOfMeasurement: { Code: "KGS" },
                            Weight: String(packages.reduce((a, p) => a + (p.weightKg || 0), 0)),
                        },
                        Package: upsPackages.length
                            ? upsPackages
                            : [
                                {
                                    PackagingType: { Code: "02" },
                                    Dimensions: { UnitOfMeasurement: { Code: "CM" }, Length: "10", Width: "8", Height: "6" },
                                    PackageWeight: { UnitOfMeasurement: { Code: "KGS" }, Weight: "2" },
                                },
                            ],
                        ShipmentRatingOptions: { NegotiatedRatesIndicator: "Y" },
                        InvoiceLineTotal: { CurrencyCode: moeda || "USD", MonetaryValue: declaredTotal },
                        DeliveryTimeInformation: { PackageBillType: "03", Pickup: { Date: pickupYMD || "20250101" } },
                    },
                },
            };

            const pesoTotalPedidoKg = getItensTotalKg(pedidoObj);

            const packagesId = cotacaoCaixas
                .map((c) => String(c.data?.id))
                .filter((id) => id && id !== "undefined" && id !== "null")
            // .join(",");

            // ===== FEDEX RATE payload mínimo =====
            const fedexRatePayload: FedexRequest = {
                pedido_ref: String(pedidoRef),
                packagesId,
                pesoTotalPedidoKg,
                shipper: remetente,
                recipient: destinatario,
            };

            // roda as duas cotações (tolerância a falha)
            let upsRateRes: any = null;
            let fedexRateRes: any = null;

            try {
                upsRateRes = await cotarUPS(upsRatePayload);
            } catch (err) {
                logAxiosError("RATE/UPS", err);
            }

            try {
                fedexRateRes = await cotarFEDEX(fedexRatePayload);
            } catch (err) {
                logAxiosError("RATE/FEDEX", err);
            }

            const rows: CompareRow[] = [];

            if (upsRateRes) {
                const rated = upsRateRes?.raw?.RateResponse?.RatedShipment;
                const list = Array.isArray(rated) ? rated : rated ? [rated] : [];
                const chosen =
                    list.find((r: any) => String(r?.Service?.Code) === String(serviceCode)) || list[0];

                console.log("UPS rate response:", chosen);
                const base = Number(chosen?.NegotiatedRateCharges?.BaseServiceCharge?.MonetaryValue) || 0;
                const negotiated =
                    Number(chosen?.NegotiatedRateCharges?.TotalCharge?.MonetaryValue) ||
                    Number(chosen?.TotalCharges?.MonetaryValue) ||
                    Number(chosen?.TransportationCharges?.MonetaryValue) ||
                    0;

                const currency =
                    chosen?.NegotiatedRateCharges?.TotalCharge?.CurrencyCode ||
                    chosen?.TotalCharges?.CurrencyCode ||
                    chosen?.TransportationCharges?.CurrencyCode ||
                    "USD";

                rows.push({
                    carrier: "UPS",
                    serviceCode,
                    label: "UPS Negotiated",
                    currency,
                    base,
                    total: negotiated,
                    carrier_raw: upsRateRes?.raw || upsRateRes, // ✅ importante
                });
            }

            if (fedexRateRes) {
                console.log("FEDEX rate response:", fedexRateRes);
                const svcs = Array.isArray(fedexRateRes?.services) ? fedexRateRes.services : [];
                const chosen =
                    svcs.find((s: any) => s?.serviceType === "FEDEX_INTERNATIONAL_CONNECT_PLUS") || svcs[0];

                console.log("FEDEX chosen rate:", chosen);
                if (chosen) {
                    // const totalUsd = Number(icp.total ?? icp.base ?? 0) || 0;

                    rows.push({
                        carrier: "FEDEX",
                        serviceCode: chosen.serviceType,
                        label: `FedEx ${chosen.serviceType}`,
                        currency: chosen.currency || "USD",
                        base: Number(chosen.base || 0),
                        total: Number(chosen.total || chosen.base || 0),
                        carrier_raw: fedexRateRes?.raw || fedexRateRes, // importante
                    });
                }
            }

            if (!rows.length) throw new Error("Nenhuma cotação disponível (UPS/FedEx falharam).");

            const respCompare = await compararFretesShipment({
                pedido_ref: pedidoRef,
                pedido: pedidoObj || {},
                caixa: cotacaoCaixas.map((c) => ({ entryId: c.entryId, ...c.data })),

                // você já calculou os rate payloads acima:
                ups_rate_payload: rows.find(r => r.carrier === "UPS")?.carrier_raw || null,
                fedex_rate_payload: rows.find(r => r.carrier === "FEDEX")?.carrier_raw || null,

                preco_base_ups: rows.find(r => r.carrier === "UPS")?.base,
                // comentario informal: usa base do rate pra nao zerar no override
                preco_base_fedex: rows.find(r => r.carrier === "FEDEX")?.base,

                serviceCode,
            });

            if (!respCompare?.data?.ok) throw new Error(respCompare?.data?.error || "Falha no compare shipment");

            setShipmentId(Number(respCompare.data.shipment_id));

            // o compare devolve o preço do cliente (com o markup do plano);
            // os rows locais só têm o valor puro da transportadora
            const quotes = respCompare?.data?.quotes || {};
            setCompareRows(
                rows.map((r) => {
                    const precoCliente = Number(quotes?.[r.carrier]?.total_cliente);
                    return Number.isFinite(precoCliente) && precoCliente > 0
                        ? { ...r, total_cliente: precoCliente }
                        : r;
                }),
            );
            setStep(3);
        } catch (err: any) {
            setCompareErro(err?.message || "Falha ao comparar fretes");
        } finally {
            setCompareLoading(false);
        }
    }

    // ========= EMISSÃO UPS =========
    async function handleEmitirUPS(selectedRow?: any, cotacaoIdOverride?: number | null) {
        setEmitErro(null);
        setEmitResp(null);
        setEmitLoading(true);

        try {
            const okPessoa = (p: any) => p.nome && p.rua && p.cidade && p.cep && p.pais;
            if (!cotacaoCaixas.length) throw new Error("Sem pacotes.");
            if (!okPessoa(remetente)) throw new Error("Cadastro do remetente incompleto.");
            if (!okPessoa(destinatario)) throw new Error("Pedido sem endereço completo.");

            const itensTotalKg = getItensTotalKg(pedidoObj);
            if (!itensTotalKg || itensTotalKg <= 0) throw new Error("Informe o peso total dos itens (kg).");

            const nPkgs = cotacaoCaixas.length || 1;
            const pesoPorCaixa = Math.max(itensTotalKg / nPkgs, 0.001);
            const packages = cotacaoCaixas.map((c) => ({
                weightKg: pesoPorCaixa,
                dimCm: {
                    height: toNum(c.data.altura) ?? 0,
                    width: toNum(c.data.largura) ?? 0,
                    length: toNum(c.data.comprimento) ?? 0,
                },
            }));

            const invoiceItems = itensArray(pedidoObj).map((it: any) => ({
                description: it.descricao || it.titulo || it.title || it.sku || "Item",
                quantity: Number(it.qty || it.quantidade || 1),
                unitPrice: Number(it.preco || it.unit_price || it.price || 0),
                hscode: it.hscode || undefined,
                countryOfOrigin: (it.pais_origem || remetente?.pais || "BR").toString().toUpperCase(),
                weightKg: num(it.peso_kg),
            }));

            const produtosCI = invoiceItems.map((p: any, i: number) => ({
                Description: p.description || `Item ${i + 1}`,
                CommodityCode: p.hscode || "00000000",
                OriginCountryCode: p.countryOfOrigin || "BR",
                Unit: {
                    UnitOfMeasurement: { Code: "PCS" },
                    Number: qtyStr(p.quantity),
                    Value: moneyStr(p.unitPrice),
                },
                UnitPrice: moneyStr(p.unitPrice),
                NetCostCode: "NC",
                PreferenceCriteria: "A",
            }));

            const declaredTotal = invoiceItems
                .reduce((acc: number, it: any) => acc + Number(it.unitPrice || 0) * Number(it.quantity || 0), 0)
                .toFixed(2);

            const upsRow = compareRows.find((r) => r.carrier === "UPS");
            const freightCurrency = upsRow?.currency || "USD";
            const freightValueForInvoice = Number(upsRow?.total || 0);
            if (!freightValueForInvoice) throw new Error("Frete não encontrado para a Invoice (reabra a comparação).");

            const serviceCodeChosen =
                selectedRow?.code || selectedRow?.serviceCode || selectedRow?.service?.code || serviceCode || "";
            if (!serviceCodeChosen) throw new Error("Serviço não selecionado.");

            const ciDate = toYMD(pickupDate) || toYMD(new Date());
            const ciNumber = `INV-${pedidoRef || Date.now()}`;
            const ciCurrency = moeda || "USD";
            const rawTax = String(remetente?.cnpjOuTaxId ?? "");
            const cnpj = onlyDigitsStr(rawTax).slice(0, 14);

            console.log(SHIPPER_NUMBER)

            const shipPayload: UpsShipRequest = {
                ShipmentRequest: {
                    Request: {
                        RequestOption: "nonvalidate",
                        TransactionReference: { CustomerContext: "", TransactionIdentifier: "String" },
                    },
                    Shipment: {
                        Description: "Description of Goods",
                        Shipper: {
                            Name: remetente?.nome || "",
                            AttentionName: remetente?.nome || "",
                            ShipperNumber: SHIPPER_NUMBER || "K9E789",
                            TaxIdentificationNumber: cnpj || undefined,
                            Phone: { Number: remetente?.telefone || "" },
                            Address: {
                                AddressLine: addrArr(remetente?.rua, remetente?.numero),
                                City: remetente?.cidade || "",
                                StateProvinceCode: (remetente?.estado || "").toUpperCase() || "",
                                PostalCode: onlyDigits(remetente?.cep),
                                CountryCode: iso2Country(remetente?.pais) || "",
                            },
                        },
                        ShipTo: {
                            Name: destinatario.nome || "",
                            AttentionName: destinatario.nome || "",
                            TaxIdentificationNumber: destinatario.cnpjOuTaxId || "",
                            Phone: { Number: destinatario.telefone || "" },
                            Address: {
                                AddressLine: addrArr(destinatario.rua, destinatario.numero),
                                City: destinatario.cidade || "",
                                StateProvinceCode: (destinatario.estado || "").toUpperCase() || "",
                                PostalCode: onlyDigits(destinatario.cep),
                                CountryCode: iso2Country(destinatario.pais) || "",
                            },
                        },
                        ShipFrom: {
                            Name: remetente?.nome || "",
                            AttentionName: remetente?.nome || "",
                            Phone: { Number: remetente?.telefone || "" },
                            TaxIdentificationNumber: cnpj || undefined,
                            Address: {
                                AddressLine: addrArr(remetente?.rua, remetente?.numero),
                                City: remetente?.cidade || "",
                                StateProvinceCode: (remetente?.estado || "").toUpperCase() || "",
                                PostalCode: onlyDigits(remetente?.cep),
                                CountryCode: iso2Country(remetente?.pais) || "",
                            },
                        },
                        PaymentInformation: {
                            ShipmentCharge: { Type: "01", BillShipper: { AccountNumber: SHIPPER_NUMBER || "K9E789" } },
                        },
                        Service: { Code: String(serviceCodeChosen), Description: "Expedited" },
                        Package: packages.map((p) => ({
                            Description: "International Goods",
                            Packaging: { Code: "02" },
                            PackageWeight: { UnitOfMeasurement: { Code: "KGS" }, Weight: String(p.weightKg || 0) },
                            Dimensions: {
                                UnitOfMeasurement: { Code: "CM" },
                                Length: String(p.dimCm.length),
                                Width: String(p.dimCm.width),
                                Height: String(p.dimCm.height),
                            },
                        })),
                        ShipmentServiceOptions: {
                            InternationalForms: {
                                FormType: "01",
                                ImageFormat: { Code: "PDF" },
                                InvoiceNumber: n2u(ciNumber),
                                InvoiceDate: n2u(ciDate),
                                CurrencyCode: n2u(ciCurrency) ?? "USD",
                                TermsOfShipment: triangulacao,
                                ReasonForExport: "SALE",
                                FreightCharges: { CurrencyCode: freightCurrency, MonetaryValue: moneyStr(freightValueForInvoice) },
                                InvoiceLineTotal: { CurrencyCode: freightCurrency, MonetaryValue: declaredTotal },
                                TaxInformation: { TaxIDType: "EIN", TaxIDNumber: cnpj },
                                Contacts: {
                                    SoldFrom: {
                                        Option: "01",
                                        Name: remetente.nome || "",
                                        AttentionName: remetente.nome || "",
                                        TaxIdentificationNumber: cnpj,
                                        Phone: { Number: remetente.telefone || "" },
                                        Address: {
                                            AddressLine: addrArr(remetente.rua, remetente.numero),
                                            City: remetente.cidade || "",
                                            StateProvinceCode: (remetente.estado || "").toUpperCase() || "",
                                            PostalCode: onlyDigits(remetente.cep),
                                            CountryCode: iso2Country(remetente.pais) || "",
                                        },
                                        EmailAddress: remetente.email || "",
                                    },
                                    SoldTo: {
                                        Option: "01",
                                        Name: destinatario.nome || "",
                                        AttentionName: destinatario.nome || "",
                                        Phone: { Number: destinatario.telefone || "" },
                                        Address: {
                                            AddressLine: addrArr(destinatario.rua, destinatario.numero),
                                            City: destinatario.cidade || "",
                                            StateProvinceCode: (destinatario.estado || "").toUpperCase() || "",
                                            PostalCode: onlyDigits(destinatario.cep),
                                            CountryCode: iso2Country(destinatario.pais) || "",
                                        },
                                    },
                                    Producer: {
                                        Option: "01",
                                        CompanyName: remetente.nome || "",
                                        Address: {
                                            AddressLine: addrArr(remetente.rua, remetente.numero),
                                            City: remetente?.cidade || "",
                                            StateProvinceCode: (remetente?.estado || "").toUpperCase() || "",
                                            PostalCode: onlyDigits(remetente?.cep),
                                            CountryCode: iso2Country(remetente?.pais) || "",
                                        },
                                        Phone: { Number: remetente?.telefone || "" },
                                        EmailAddress: remetente.email || "",
                                        TaxIdentificationNumber: cnpj || undefined,
                                    },
                                },
                                Product: produtosCI,
                            },
                        },
                    },
                    RateInformation: { NegotiatedRatesIndicator: "Y" },
                    LabelSpecification: { LabelImageFormat: { Code: "PDF" } },
                    LabelDelivery: { LabelLinksIndicator: "" },
                },
            };

            const attachId = Number(cotacaoIdOverride ?? cotacaoId ?? 0) || null;
            const shipRaw = await emitirUPS({ ...shipPayload, cotacaoId: attachId });
            const lbl = shipRaw.label;
            const invoiceB64: string | undefined = shipRaw?.invoice?.b64 || undefined;
            const invoiceMime: string | undefined = shipRaw?.invoice?.mime || (invoiceB64 ? "application/pdf" : undefined);

            setSelectedCarrier("UPS");

            setEmitResp({
                carrier: "UPS",
                trackingNumbers: shipRaw.trackingNumbers || [],
                labelB64: lbl && typeof (lbl as any).b64 === "string" ? (lbl as any).b64 : undefined,
                labelType: lbl ? lbl.type : undefined,
                invoiceB64,
                invoiceMime,
            });

            try {
                const light = {
                    carrier: "UPS",
                    status: "label_ready",
                    rate_result: { services: ratingResp?.services ?? [] },
                    ship_result: {
                        trackingNumbers: shipRaw.trackingNumbers,
                        label: shipRaw.label ? { type: shipRaw.label.type } : null,
                    },
                };
                const { data: saved } = await criarRemessa(light);
                setShipmentId(saved.id);
            } catch (e) {
                console.warn("[criarRemessa] falhou:", e);
            }

            try {
                if (attachId) {
                    const tn = (shipRaw?.trackingNumbers && shipRaw.trackingNumbers[0]) || "";
                    const isB64 = lbl && typeof (lbl as any).b64 === "string";
                    const label_base64 = isB64 ? ((lbl as any).b64 as string) : undefined;
                    const label_type = lbl?.type as "PNG" | "GIF" | "ZPL" | "PDF" | undefined;
                    const label_mime =
                        label_type === "PNG"
                            ? "image/png"
                            : label_type === "GIF"
                                ? "image/gif"
                                : label_type === "ZPL"
                                    ? "text/plain"
                                    : label_type === "PDF"
                                        ? "application/pdf"
                                        : undefined;

                    await anexarDocsCotacao(attachId, {
                        etiqueta_base64: label_base64,
                        etiqueta_mime: label_mime,
                        tracking_number: tn || undefined,
                        invoice_base64: invoiceB64,
                        invoice_mime: invoiceMime || "application/pdf",
                    });
                } else {
                    console.warn("[UPS] cotacaoId vazio, nao vai anexar docs/tracking");
                }
            } catch (err) {
                logAxiosError("ANEXAR/DOCS/COTACAO", err);
            }

            const tn = shipRaw.trackingNumbers?.[0];
            if (tn) setTracking(tn);
            setStep(4);
        } catch (err: any) {
            logAxiosError("EMITIR/UPS", err);
            setEmitErro(err?.message || "Falha ao emitir");
        } finally {
            setEmitLoading(false);
        }
    }

    // ========= EMISSÃO FEDEX =========
    async function handleEmitirFEDEX(selectedRow?: any, cotacaoIdOverride?: number | null) {
        setEmitErro(null);
        setEmitResp(null);
        setEmitLoading(true);

        try {
            const itensTotalKg = getItensTotalKg(pedidoObj);
            if (!itensTotalKg || itensTotalKg <= 0) throw new Error("Informe o peso total dos itens (kg).");

            const packagesId = cotacaoCaixas
                .map((c) => String(c.data?.id))
                .filter((id) => id && id !== "undefined" && id !== "null")
            // .join(",");

            const invoiceItems = itensArray(pedidoObj);

            if (!invoiceItems.length) {
                throw new Error("Pedido sem itens. FedEx precisa de commodities (itens) para desembaraço.");
            }

            const items = invoiceItems.map((it: any) => {
                const qty = Number(it.qty || it.quantidade || 1) || 1;
                const unit = Number(it.preco || it.unit_price || it.price || 0) || 0;

                // aqui é CRÍTICO: harmonized/hs code precisa existir (ou você bloqueia)
                const hs = String(it.hscode || it.hsCode || it.harmonizedCode || "").replace(/\D/g, "");

                if (!hs) {
                    const name = it.descricao || it.titulo || it.title || it.sku || "Item";
                    throw new Error(`Item sem HS Code: ${name}. Preencha o HS Code para emitir via FedEx.`);
                }

                return {
                    description: String(it.descricao || it.titulo || it.title || it.sku || "Item").slice(0, 45),
                    hsCode: hs,
                    quantity: qty,
                    quantityUnits: "PCS",
                    unitValue: unit,
                    totalValue: unit * qty,
                    currency: "USD",
                    originCountry: String(it.pais_origem || remetente?.pais || "BR").toUpperCase(),
                    weightKg: Number(it.peso_kg || (it.grams != null ? Number(it.grams) / 1000 : 0.1)) || 0.1,
                };
            });


            const fxAttachId = Number(cotacaoIdOverride ?? cotacaoId ?? 0) || null;
            const shipPayload: FedexRequest = {
                pedido_ref: String(pedidoRef),
                packagesId,
                pesoTotalPedidoKg: itensTotalKg,
                freight_total: Number(selectedRow?.total || 0) || undefined,
                rate_payload: selectedRow?.carrier_raw || undefined,
                shipper: remetente,
                recipient: destinatario,
                cotacaoId: fxAttachId,
            };

            const shipRaw = await emitirFEDEX(shipPayload);

            const tns = fedexExtractTrackingNumbers(shipRaw);
            const art = extractFedexArtifacts(shipRaw);

            setSelectedCarrier("FEDEX");

            setEmitResp({
                carrier: "FEDEX",
                trackingNumbers: tns,
                labelUrl: art.labelUrl,
                invoiceUrl: art.invoiceUrl,
            });

            // Se seu anexarDocsCotacao HOJE só aceita base64, não manda docs aqui.
            // (Se você adicionar suporte a URL no backend, aí você manda etiqueta_url/invoice_url)
            try {
                const fxCotId = Number(cotacaoIdOverride ?? cotacaoId ?? 0) || null;
                if (fxCotId) {
                    await anexarDocsCotacao(fxCotId, {
                        tracking_number: tns?.[0],
                        etiqueta_url: art.labelUrl,
                        invoice_url: art.invoiceUrl,
                    } as any);
                } else {
                    console.warn("[FEDEX] cotacaoId vazio, nao vai anexar docs/tracking");
                }
            } catch (err) {
                logAxiosError("ANEXAR/DOCS/COTACAO/FEDEX", err);
            }

            const tn = tns?.[0];
            if (tn) setTracking(tn);
            setStep(4);
        } catch (err: any) {
            logAxiosError("EMITIR/FEDEX", err);
            setEmitErro(err?.message || "Falha ao emitir FedEx");
        } finally {
            setEmitLoading(false);
        }
    }

    // ========= Tracking =========
    async function handleTrack(e?: FormEvent) {
        e?.preventDefault();
        setTrackErro(null);
        setTrackResp(null);

        if (!tracking) return setTrackErro("Informe o tracking.");
        setTrackLoading(true);

        try {
            const carrier = emitResp?.carrier || selectedCarrier || "UPS";
            const data = carrier === "FEDEX" ? await rastrearFEDEX(tracking) : await rastrearUPS(tracking);

            setTrackResp(data);
            if (shipmentId) await atualizarTrackRemessa(shipmentId, data);
        } catch (err: any) {
            logAxiosError("TRACK", err);
            setTrackErro(err?.message || "Falha ao rastrear");
        } finally {
            setTrackLoading(false);
        }
    }

    return (
        <div className="ups-wizard">
            {step === 1 && (
                <RateStep
                    moeda={moeda}
                    setMoeda={setMoeda}
                    pickupDate={pickupDate}
                    serviceCode={serviceCode}
                    setServiceCode={setServiceCode}
                    setPickupDate={setPickupDate}
                    pedidoRef={pedidoRef}
                    setPedidoRef={setPedidoRef}
                    setPedidoObj={setPedidoObj}
                    pedidoObj={pedidoObj}
                    todasCaixas={todasCaixas}
                    codSelecionado={codSelecionado}
                    setCodSelecionado={setCodSelecionado}
                    cotacaoCaixas={cotacaoCaixas}
                    addCaixa={addCaixa}
                    removeCaixa={removeCaixa}
                    shipper={remetente}
                    destinatario={destinatario}
                    onNext={() => setStep(2)}
                />
            )}

            {step === 2 && (
                <ShipStep
                    shipper={remetente}
                    setShipper={setRemetente}
                    destinatario={destinatario}
                    setDestinatario={setDestinatario}
                    paymentBill={paymentBill}
                    setPaymentBill={setPaymentBill}
                    triangulacao={triangulacao}
                    setTriangulacao={setTriangulacao}
                    loading={compareLoading}
                    onBack={() => setStep(1)}
                    onNext={handleCompare}
                />
            )}

            {step === 3 && (
                <CompareStep
                    rows={compareRows}
                    loading={compareLoading}
                    erro={compareErro}
                    onBack={() => setStep(2)}
                    onSelectCarrier={async (row) => {
                        try {
                            if (!shipmentId) throw new Error("shipmentId vazio (faça compare novamente).");

                            setCompareLoading(true);
                            setCompareErro(null);

                            const resp = await confirmarFreteShipment(shipmentId, row.carrier);
                            if (!resp?.data?.ok) throw new Error(resp?.data?.error || "Falha ao confirmar shipment");

                            // agora você tem a cotação criada/recuperada
                            const newCotacaoId = Number(resp.data.cotacao_id) || null;
                            setCotacaoId(newCotacaoId);

                            // só depois emite
                            if (row.carrier === "UPS") await handleEmitirUPS(row, newCotacaoId);
                            if (row.carrier === "FEDEX") await handleEmitirFEDEX(row, newCotacaoId);
                        } catch (e: any) {
                            setCompareErro(e?.message || "Erro ao confirmar frete");
                        } finally {
                            setCompareLoading(false);
                        }
                    }}

                />
            )}

            {step === 4 && (
                <TrackStep
                    tracking={tracking}
                    setTracking={setTracking}
                    trackLoading={trackLoading}
                    trackErro={trackErro}
                    trackResp={trackResp}
                    onSubmit={handleTrack}
                    onBack={() => setStep(3)}
                />
            )}
        </div>
    );
}

